import { createSlice } from "@reduxjs/toolkit";

//name : string - Slice name
//thunk : AsyncThunk - Async thunk to handle
//listMode : boolean - true if slice manages list + pagination
//resetActionName : string|null - Optional reset action name

export const createDynamicSlice = ({
  name,
  thunk,
  listMode,
  resetActionName = null,
}) => {
  const initialState = listMode
    ? {
        list: [], // default empty array
        pageInfo: null,
        count: null,
        loading: false,
        error: null,
        loadedOnce: false,
      }
    : {
        data: null,
        loading: false,
        error: null,
        loadedOnce: false,
      };

  const reducers = resetActionName
    ? {
        [resetActionName]: (state) => {
          state.loading = false;
          state.error = null;
          state.loadedOnce = false;
          if (listMode) {
            state.list = [];
            state.pageInfo = null;
            state.count = null;
          } else {
            state.data = null;
          }
        },
      }
    : {};

  return createSlice({
    name,
    initialState,
    reducers,
    extraReducers: (builder) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading = false;
          state.loadedOnce = true;
          if (listMode) {
            // Standard APIs: { data, pagination, totalCount }
            if (Array.isArray(action.payload?.data)) {
              state.list = action.payload.data;
              state.pageInfo = action.payload.pagination ?? null;
              state.count = action.payload.totalCount ?? null;
            }

            // Legacy / special APIs: { response, totalCount }
            else if (Array.isArray(action.payload?.response)) {
              state.list = action.payload.response;
              state.pageInfo = null;
              state.count =
                action.payload.totalCount ?? action.payload.response.length;
            }

            // Safety fallback
            else {
              state.list = [];
              state.pageInfo = null;
              state.count = null;
            }
          } else {
            state.data = action.payload;
          }
          state.loadedOnce = true;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.loadedOnce = true;

          if (action.payload) {
            state.error =
              action.payload.message || JSON.stringify(action.payload);
          } else {
            state.error = action.error?.message || "Something went wrong";
          }
          if (listMode) {
            state.list = [];
            state.pageInfo = null;
            state.count = null;
          } else {
            state.data = null;
          }
        });
    },
  });
};

//thunk : AsyncThunk - Async thunk
//key : string - Name of the object to store data
//dataKey : string - Key to store array data (default 'data')
//resetActionName : string|null - Optional reset action

export const createMappedContentSlice = (
  thunk,
  key = "contents",
  dataKey = "data",
  resetActionName = null
) => {
  const initialState = { [key]: {} };

  return createSlice({
    name: thunk.typePrefix,
    initialState,
    reducers: resetActionName
      ? {
          [resetActionName]: (state) => {
            state[key] = {};
          },
        }
      : {},
    extraReducers: (builder) => {
      builder
        .addCase(thunk.pending, (state, action) => {
          const contentType = action.meta.arg.contentType;
          state[key][contentType] = {
            ...(state[key][contentType] || {}),
            loading: true,
            error: null,
          };
        })
        .addCase(thunk.fulfilled, (state, action) => {
          const { contentType, data } = action.payload;
          state[key][contentType] = {
            ...(Array.isArray(data) ? { [dataKey]: data } : data),
            loading: false,
            error: null,
          };
        })
        .addCase(thunk.rejected, (state, action) => {
          const contentType =
            action.payload?.contentType || action.meta.arg.contentType;
          state[key][contentType] = {
            ...(state[key][contentType] || {}),
            loading: false,
            error:
              action.payload?.message || action.error?.message || "data error",
          };
        });
    },
  });
};
